<template>
  <div class="lab-modal flat-scroll">
    <div class="lab-modal__modal">
      <div class="lab-modal__wrapper">
        <div class="lab-modal__content-wrapper">
          <TitleBarLab
            :lab="lab"
            @closeModal="$emit('closeModal')"
            @startIssue="startIssue"
          />
          <div class="lab-modal__body">
            <div class="lab-modal__navbar"></div>
            <div class="lab-modal__left">
              <div class="lab-modal__description">
                <div
                  class="lab-modal__explanation-video"
                  v-if="currentLab.explanation_video"
                >
                  <IsolatedVideo
                    :videoId="currentLab.explanation_video"
                    :autostart="false"
                    trigger="lab"
                  />
                </div>
                <div
                  class="lab-modal__description-wrapper markdown-styles"
                  ref="description"
                  v-html="lab.description_html"
                ></div>
              </div>
            </div>
            <div class="lab-modal__right">
              <ELSCard
                v-if="lab.lab_type === 'els'"
                :lab="currentLab"
                :setLabStatus="setLabStatus"
                :isLoading="isLoading"
                @closeModal="$emit('closeModal')"
              />
              <Card
                v-else
                :isLoading="isLoading"
                :lab="currentLab"
                :setLabStatus="setLabStatus"
              />
              <div
                class="solution-button-wrapper"
                v-if="currentLab.solution_video"
              >
                <a
                  class="solution-button button transparent"
                  @click.stop="toggleVideoPlayer()"
                >
                  REVEAL SOLUTION
                </a>
              </div>
            </div>
          </div>
          <SingleVideo
            v-if="isPlayingSolutionVideo && currentLab.solution_video"
            :videoId="currentLab.solution_video"
            trigger="lab"
            @updateStatus="updateStatus"
            @closeVideoPlayer="toggleVideoPlayer"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { highlightAllUnder } from 'prismjs'
import SingleVideo from '@/components/media/SingleVideo.vue'
import IsolatedVideo from '@/components/media/IsolatedVideo.vue'
import CONSTANTS from '@/constants'
import TitleBarLab from './TitleBarLab.vue'
import Card from './Card.vue'
import ELSCard from './ELSCard.vue'

import '@/assets/css/prism-theme.css'
import 'prismjs/components/prism-python'

export default {
  name: 'LabModal',
  components: {
    TitleBarLab,
    Card,
    SingleVideo,
    IsolatedVideo,
    ELSCard,
  },
  props: {
    lab: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      isPlayingSolutionVideo: false,
      hasUpdatedStatus: false,
      isLoading: false,
    }
  },
  computed: {
    ...mapState({
      currentLab: state => state.labs.currentLab,
      currentLabSession: state => state.labs.currentLabSession,
      currentCourse: state => state.courses.currentCourse,
    }),
  },
  methods: {
    async setLabStatus(status = CONSTANTS.CONTENT_USER_STATUS.STARTED) {
      await this.$store.dispatch('content/updateLabStatus', {
        labId: this.lab.id,
        status,
      })
      if (status === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_LAB_STARTED, {
          course_id: this.currentCourse.id,
          course_name: this.currentCourse.name,
          lab_id: this.currentLab.id,
          lab_name: this.currentLab.name,
          ...this.currentLab,
        })
      } else if (status === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_LAB_FINISHED, {
          course_id: this.currentCourse.id,
          course_name: this.currentCourse.name,
          lab_id: this.currentLab.id,
          lab_name: this.currentLab.name,
          ...this.currentLab,
        })
      }
    },
    updatePrism() {
      this.$nextTick().then(() => {
        highlightAllUnder(this.$refs.description)
        if (window.MathJax) {
          window.MathJax.Hub.Config({
            tex2jax: {
              inlineMath: [['$', '$']],
              displayMath: [['$$', '$$']],
              processEscapes: true,
              processEnvironments: true,
            },
            // Center justify equations in code and markdown cells. Elsewhere
            // we use CSS to left justify single line equations in code cells.
            displayAlign: 'center',
            'HTML-CSS': {
              styles: { '.MathJax_Display': { margin: 0 } },
              linebreaks: { automatic: true },
            },
          })
          window.MathJax.Hub.Queue([
            'Typeset',
            window.MathJax.Hub,
            this.$refs.description,
          ])
        }
      })
    },
    toggleVideoPlayer() {
      this.isPlayingSolutionVideo = !this.isPlayingSolutionVideo
    },
    async updateStatus() {
      if (!this.hasUpdatedStatus && this.videoId) {
        this.hasUpdatedStatus = true
        const videoIdAndStatus = {
          videoId: this.videoId,
          status: CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        }
        await this.$store.dispatch(
          'content/updateVideoStatus',
          videoIdAndStatus
        )
        this.$store.dispatch('content/updateLegacyStatus', {
          userId: this.profile.content_api_id,
          videoId: this.videoId,
        })
      }
    },
    startIssue() {
      let payload = {}
      if (this.lab.lab_type === 'els') {
        payload = {
          els_lab_id: this.currentLab.metadata.els_lab_id,
          els_lab_status: this.currentLabSession
            ? this.currentLabSession.status
            : null,
          els_lab_session_id: this.currentLabSession
            ? this.currentLabSession.id
            : null,
        }
      } else {
        payload = {
          azure_lab_github_url: this.currentLab.external_url,
        }
      }
      this.$store.dispatch('courses/saveContentIssue', {
        content_type: 'lab',
        content_subtype: this.currentLab.lab_type,
        content_name: this.lab.name,
        content_id: this.lab.id,
        extra_args: {
          ...payload,
        },
      })
    },
  },
  async created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    // We may need some extra information that comes in the single retrieve endpoint
    this.isLoading = true
    await this.$store.dispatch('labs/getLabById', this.lab.id)
    this.isLoading = false
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  mounted() {
    this.updatePrism()
  },
  watch: {
    lab() {
      this.updatePrism()
    },
  },
}
</script>

<style lang="scss">
.lab-modal {
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--dark-gray);
    z-index: 205;
    overflow-y: auto;
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 206;
    position: relative;
  }
  &__content-wrapper {
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    max-width: 2800px;
    height: 100%;
  }
  &__left {
    padding: 30px 40px;
    background: #2c2c2c;
  }
  &__description {
    color: white;
    font-size: 16px;
    a {
      color: var(--blue);
    }
    code {
      color: white;
      background: #1e1e1e;
      padding: 5px 7px;
      border-radius: 10px;
      font-size: 0.9em;
      overflow: auto;
      display: block;
    }
    .prism-code,
    .codehilite {
      code {
        background: black;
      }
    }
    .codehilite {
      font-size: 0.95em;
      background: black;
      height: auto;
      display: block;
      width: 100%;
      margin-bottom: 20px;
      margin-top: 20px;
      overflow: initial;
      .token {
        white-space: break-spaces;
      }
    }
    table {
      background: #3d3d3d;
      border-radius: 0.25em;
      border-collapse: collapse;
      margin: 1em;
      margin-bottom: 20px;
    }
    th {
      border-bottom: 1px solid #364043;
      color: var(--orange);
      font-size: 0.85em;
      font-weight: 600;
      padding: 0.5em 1em;
      text-align: left;
    }
    td {
      color: #fff;
      font-weight: 400;
      padding: 0.65em 1em;
    }
    tbody tr {
      transition: all 0.25s ease;
    }
    tbody tr:hover {
      background: #333333;
    }
  }
  &__right {
    padding: 50px;
    align-items: center;
    text-align: center;
    .solution-button-wrapper {
      width: 500px;
      margin-top: 20px;
      display: flex;
      .solution-button {
        margin-left: auto;
        cursor: pointer;
        border-radius: 15px;
        padding: 5px 10px;
        font-size: 11px;
        color: white;
        border: 1px solid var(--orange);
        margin-top: 10px;
      }
    }
  }

  &__navbar {
    border-right: 1px solid #424040;
  }

  @include tablet {
    &__navbar {
      flex: 0 0 100px;
    }

    &__body {
      display: flex;
      width: 100%;
      height: calc(100% - 100px);
      overflow: hidden;
    }
    &__left {
      width: 45%;
      max-height: calc(100vh - 100px);
      overflow: auto;
    }
    &__right {
      flex: 1;
      width: 45%;
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 100px);
      overflow: auto;
    }
  }

  &__description {
    &-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      flex: 1;
      img {
        max-width: 100%;
      }
    }

    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .lds-ring {
    display: inline-block;
    position: relative;
    width: 15px;
    height: 15px;
    padding-top: 2px;
    div {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 15px;
      height: 15px;
      border: 2px solid rgb(129, 129, 129);
      border-radius: 50%;
      animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: rgb(129, 129, 129) transparent transparent transparent;
    }
    div:nth-child(1) {
      animation-delay: -0.45s;
    }
    div:nth-child(2) {
      animation-delay: -0.3s;
    }
    div:nth-child(3) {
      animation-delay: -0.15s;
    }
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
